import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgProgress } from '@ngx-progressbar/core';
import { isNullOrUndefined } from '@swimlane/ngx-datatable/release/utils';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationComponent } from '../../../../shared/components/confirmation/confirmation.component';
import { ViewableComponent } from '../../../../shared/components/interfaces/viewable.component';
import { ConfirmationModel } from '../../../../shared/model/confirmation.model';
import { UIDialogService } from '../../../../shared/services/ui-dialog.service';
import { AssetService } from '../../../asset/service/asset.service';
import { AuthService } from '../../../security/service/auth.service';
import { ProductUIMessages } from '../../model/product-ui.messages';
import { ProductWarrantyDuration } from '../../model/product_warranty_duration';
import { KeywordService } from '../../service/keyword.service';
import { ProductService } from '../../service/product.service';
import { ProductDeactivateComponent } from '../deactivate/product-deactivate.component';
import { ProductBaseComponent } from '../product-base.component';

@Component({
  selector: 'product-view',
  templateUrl:
    './console/app-js/modules/product/components/view/product-view.component.html'
})
export class ProductViewComponent extends ProductBaseComponent
  implements ViewableComponent {
  private loadingProduct = true;
  private deleting = false;
  private canClick = false;
  /**
   * @constructor
   * @param {ProductService} productService
   */
  constructor(
    toastr: ToastrService,
    progressService: NgProgress,
    authService: AuthService,
    productService: ProductService,
    assetService: AssetService,
    private activatedRoute: ActivatedRoute,
    private dialogService: UIDialogService,
    private router: Router,
    private keywordService: KeywordService
  ) {
    super(progressService, toastr, authService, productService, assetService);
    this.partAndProductModel.id = this.activatedRoute.snapshot.params['id'];
    // this.loadAllVendors();
    this.loadProduct();
    this.getProductWarrantyDetails(this.partAndProductModel.id);
  }

  /**
   * Loads the product details by using the given product Id.
   */
  private loadProduct(): void {
    this.progressRef.start();
    this.productService
      .getBasicProductProperties(this.partAndProductModel.id)
      .subscribe(
        (res: any) => {
          if(res){
            this.partAndProductModel = res;
            this.partAndProductModel.isActive = this.productService.isProductActive(
              this.partAndProductModel.endDate
            );
            if (this.authService.isAdmin()) {
              this.canClick = true;
            }
          }
          this.loadingProduct = false;
          this.progressRef.complete();
        },
        err => {
          this.onAPIError(ProductUIMessages.LOAD_PRODUCT_ERROR);
        }
      );
  }

  /**
   * Returns the current date.
   */
  protected getCurrentDate(): Date {
    let today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  /**
   * Redirects user to Create state with a copy of the product.
   */
  onCopy(): void {}

  /**
   * Deactivate current product.
   */
  onDeactivate(): void {
    this.partAndProductModel.endDate = new Date();
    this.productService.deleteProduct(this.partAndProductModel.id).subscribe(
      (res: any) => {
        this.deleting = false;
        this.onAPISuccess(ProductUIMessages.DEACTIVATE_PRODUCT_SUCCESS);
        this.loadProduct();
      },
      () => this.onAPIError(ProductUIMessages.DEACTIVATE_PRODUCT_ERROR)
    );
  }

  /**
   * Delete current item.
   */
  onDelete(): void {
    this.deleting = true;
    this.dialogService
      .open(ProductDeactivateComponent, [
        { provide: 'products', useValue: [this.partAndProductModel] }
      ])
      .then((result: any) => {
        if (result.associateKeywords) {
          this.associateKeywordsAndDeactivateProduct(result.productsSelected);
        } else {
          this.onDeactivate();
        }
      })
      .catch((res: any) => {
        this.deleting = false;
      });
  }

  /**
   * 1. API calls to associate deactivated make/model keywords.
   * 2. API call to deactivate Product.
   */
  private associateKeywordsAndDeactivateProduct(
    activeProducts: Array<any>
  ): void {
    const productsToBeDeactivated = [
      {
        model: this.partAndProductModel.model,
        make: this.partAndProductModel.make.name
      }
    ];
    this.keywordService
      .createKeywordAssociationForProductDeactivation(
        productsToBeDeactivated,
        activeProducts.map(product => product.id)
      )
      .subscribe(results => {
        this.checkForFailedKeywordAssociations(results, activeProducts);
        this.onDeactivate();
      });
  }

  /**
   * Check if some associations were not successful and inform the user.
   * @param results: Array
   * @param products: Array
   */
  private checkForFailedKeywordAssociations(
    results: Array<any>,
    products: Array<any>
  ): void {
    let failedAssociations = new Set();
    if (results && results.length > 0) {
      results.forEach(result => {
        if (result && result.status === 'failed') {
          failedAssociations.add(result.productId);
        }
      });
      if (failedAssociations.size > 0) {
        const message = this.keywordService.buildKeywordAssociationErrorMessage(
          Array.from(failedAssociations),
          products
        );
        this.toastr.error(message);
      }
    }
  }

  /**
   * Activate current product.
   */
  onActivate(): void {
    const confirmationMessage =
      'Are you sure you want to activate this product?';
    this.dialogService
      .open(ConfirmationComponent, [
        {
          provide: ConfirmationModel,
          useValue: new ConfirmationModel(
            'Activate Product',
            confirmationMessage
          )
        }
      ])
      .then(confirmed => {
        if (confirmed) {
          this.partAndProductModel.endDate = null;
          this.productService.saveProduct(this.partAndProductModel).subscribe(
            (res: any) => {
              this.onAPISuccess(ProductUIMessages.ACTIVATE_PRODUCT_SUCCESS);
              this.loadProduct();
            },
            (error: any) =>
              this.onAPIError(ProductUIMessages.ACTIVATE_PRODUCT_ERROR)
          );
        }
      })
      .catch((result: any) => {
        // ..
      });
  }

  /**
   * Redirects user to Edit state of a product.
   */
  onEdit(): void {
    this.router.navigate([
      '/console/product',
      this.partAndProductModel.id,
      'edit'
    ]);
  }

  onCancel(): void {
    // Not supported yet in this module.
  }

  getProductWarrantyDetails(productId: number): ProductWarrantyDuration {
    this.productService
      .getProductDurationDaysById(productId)
      .subscribe((res: any) => {
        if (res) {
          this.productWarrantyDuration.productId = res.productId;
          this.productWarrantyDuration.laborWarrantyDays = res.laborWarrantyDays;
          this.productWarrantyDuration.partsWarrantyDays = res.partsWarrantyDays;
        }
      });
    if (isNullOrUndefined(this.productWarrantyDuration.laborWarrantyDays == null)) {
      this.productWarrantyDuration.laborWarrantyDays = 0;
    }
    if (isNullOrUndefined(this.productWarrantyDuration.partsWarrantyDays == null)) {
      this.productWarrantyDuration.partsWarrantyDays = 0;
    }
    return this.productWarrantyDuration;
  }
}
